import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { createQuote, listQuotes, type QuoteSummary } from '@/api/quotes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

function money(v: number, currency: string, lang: string): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(v);
}

const STATUS_CLASS: Record<string, string> = {
  DRAFT: 'text-[var(--color-lop-text-muted)]',
  SENT: 'text-[var(--color-lop-teal)]',
  VIEWED: 'text-[var(--color-lop-teal)]',
  ACCEPTED: 'text-green-400',
  DECLINED: 'text-[var(--color-lop-danger)]',
  REJECTED: 'text-[var(--color-lop-danger)]',
  EXPIRED: 'text-[var(--color-lop-text-muted)]',
  SUPERSEDED: 'text-[var(--color-lop-text-muted)]',
  DELETED: 'text-[var(--color-lop-text-muted)]',
};

export function QuotesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  function load() {
    setLoading(true);
    listQuotes()
      .then(setQuotes)
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  return (
    <div className="p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('quotes.title')}</h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('quotes.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="quotes-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('quotes.new')}
        </Button>
      </header>

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {!loading && quotes.length === 0 && (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">{t('quotes.empty')}</p>
      )}
      {!loading && quotes.length > 0 && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--color-lop-border)] text-[10px] uppercase tracking-wide text-[var(--color-lop-text-muted)]">
                <th className="px-4 py-2">{t('quotes.cols.number')}</th>
                <th className="px-4 py-2">{t('quotes.cols.title')}</th>
                <th className="px-4 py-2">{t('quotes.cols.status')}</th>
                <th className="px-4 py-2 text-right">{t('quotes.cols.total')}</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  className="cursor-pointer border-b border-[var(--color-lop-border)]/40 hover:bg-[var(--color-lop-bg-card-hi)]"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                  data-testid="quotes-row"
                >
                  <td className="px-4 py-2.5 font-mono text-xs">{q.quoteNumber}</td>
                  <td className="px-4 py-2.5">{q.title}</td>
                  <td className={`px-4 py-2.5 ${STATUS_CLASS[q.status] ?? ''}`}>{t(`quotes.status.${q.status}`)}</td>
                  <td className="px-4 py-2.5 text-right">{money(q.total, q.currency, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewQuoteModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`/quotes/${id}`);
          }}
        />
      )}
    </div>
  );
}

function NewQuoteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<{ id: string; name: string }[]>('/accounts')
      .then(({ data }) => setAccounts(data ?? []))
      .catch(() => setAccounts([]));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { id } = await createQuote({ title: title.trim(), accountId });
      onCreated(id);
    } catch {
      setError(t('quotes.createError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('quotes.newModal.title')}</h3>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('quotes.cols.title')}</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="mb-4" data-testid="quotes-new-title" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">{t('quotes.account')}</label>
        <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} required className="mb-4">
          <option value="">{t('quotes.selectAccount')}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        {error && <p className="mb-3 text-sm text-[var(--color-lop-danger)]">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={submitting || !title.trim() || !accountId} data-testid="quotes-new-submit">
            {t('quotes.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
